import type { ReactNode } from 'react';

type TextRange = {
  start: number;
  end: number;
};

const SEARCH_HIGHLIGHT_ELLIPSIS = '...';
const SEARCH_SNIPPET_CONTEXT_LENGTH = 18;

const sortSearchTerms = (searchTerms: string[]): string[] =>
  [...searchTerms].sort((leftTerm, rightTerm) => rightTerm.length - leftTerm.length);

export const getSearchRanges = (text: string, searchTerms: string[]): TextRange[] => {
  const sortedSearchTerms = sortSearchTerms(searchTerms);

  if (!sortedSearchTerms.length) {
    return [];
  }

  const normalizedText = text.toLocaleLowerCase();
  const ranges: TextRange[] = [];

  sortedSearchTerms.forEach(searchTerm => {
    const normalizedSearchTerm = searchTerm.toLocaleLowerCase();

    if (!normalizedSearchTerm) {
      return;
    }

    let searchStart = 0;

    while (searchStart < normalizedText.length) {
      const matchIndex = normalizedText.indexOf(normalizedSearchTerm, searchStart);

      if (matchIndex === -1) {
        return;
      }

      ranges.push({
        start: matchIndex,
        end: matchIndex + normalizedSearchTerm.length,
      });

      searchStart = matchIndex + normalizedSearchTerm.length;
    }
  });

  if (!ranges.length) {
    return [];
  }

  ranges.sort((leftRange, rightRange) => leftRange.start - rightRange.start || leftRange.end - rightRange.end);

  return ranges.reduce<TextRange[]>((mergedRanges, currentRange) => {
    const previousRange = mergedRanges.at(-1);

    if (!previousRange || currentRange.start > previousRange.end) {
      mergedRanges.push(currentRange);
      return mergedRanges;
    }

    previousRange.end = Math.max(previousRange.end, currentRange.end);
    return mergedRanges;
  }, []);
};

const isWhitespace = (value: string): boolean => /\s/.test(value);

const getWordStart = (text: string, index: number): number => {
  let currentIndex = index;

  while (currentIndex > 0 && !isWhitespace(text[currentIndex - 1])) {
    currentIndex -= 1;
  }

  return currentIndex;
};

const getWordEnd = (text: string, index: number): number => {
  let currentIndex = index;

  while (currentIndex < text.length && !isWhitespace(text[currentIndex])) {
    currentIndex += 1;
  }

  return currentIndex;
};

export const buildFocusedSearchText = (text: string, searchTerms: string[]): string => {
  const searchRanges = getSearchRanges(text, searchTerms);

  if (!searchRanges.length) {
    return text;
  }

  const [firstRange] = searchRanges;
  const focusStart = getWordStart(text, firstRange.start);
  const focusEnd = getWordEnd(text, firstRange.end);
  const snippetEnd = getWordEnd(text, Math.min(text.length, focusEnd + SEARCH_SNIPPET_CONTEXT_LENGTH));

  if (focusStart === 0 && snippetEnd >= text.length) {
    return text;
  }

  const snippet = text.slice(focusStart, snippetEnd).trim();

  if (!snippet) {
    return text;
  }

  const prefix = focusStart > 0 ? SEARCH_HIGHLIGHT_ELLIPSIS : '';
  const suffix = snippetEnd < text.length ? SEARCH_HIGHLIGHT_ELLIPSIS : '';

  return `${prefix}${snippet}${suffix}`;
};

export type FocusedSearchSlice = {
  start: number;
  end: number;
  hasPrefixEllipsis: boolean;
  hasSuffixEllipsis: boolean;
};

export const getFocusedSearchSlice = (text: string, searchTerms: string[]): FocusedSearchSlice | null => {
  const searchRanges = getSearchRanges(text, searchTerms);

  if (!searchRanges.length) {
    return null;
  }

  const [firstRange] = searchRanges;
  const focusStart = getWordStart(text, firstRange.start);
  const focusEnd = getWordEnd(text, firstRange.end);
  const snippetEnd = getWordEnd(text, Math.min(text.length, focusEnd + SEARCH_SNIPPET_CONTEXT_LENGTH));

  return {
    start: focusStart,
    end: snippetEnd,
    hasPrefixEllipsis: focusStart > 0,
    hasSuffixEllipsis: snippetEnd < text.length,
  };
};

export const renderHighlightedText = (text: string, searchTerms: string[], highlightClassName: string): ReactNode => {
  const searchRanges = getSearchRanges(text, searchTerms);

  if (!searchRanges.length) {
    return text;
  }

  const highlightedSegments: ReactNode[] = [];
  let currentIndex = 0;

  searchRanges.forEach((searchRange, rangeIndex) => {
    if (currentIndex < searchRange.start) {
      highlightedSegments.push(text.slice(currentIndex, searchRange.start));
    }

    highlightedSegments.push(
      <span key={`${searchRange.start}-${searchRange.end}-${rangeIndex}`} className={highlightClassName}>
        {text.slice(searchRange.start, searchRange.end)}
      </span>,
    );

    currentIndex = searchRange.end;
  });

  if (currentIndex < text.length) {
    highlightedSegments.push(text.slice(currentIndex));
  }

  return highlightedSegments;
};
